import { useEffect, useState } from 'react'
import { X, Calendar, User as UserIcon, MessageSquare, Trash2 } from 'lucide-react'
import { apiGet } from '../../services/api'
import {
  useProjectStore,
  type Task,
  type TaskStatus,
  type TaskPriority,
  type TaskComment,
  TASK_STATUS_ORDER,
  TASK_PRIORITIES,
} from '../../store/projectStore'

interface TaskDetailModalProps {
  isOpen: boolean
  onClose: () => void
  task: Task | null
  projectId: number
  canEdit: boolean
  canDelete: boolean
}

interface UserOption {
  id: number
  name: string
  email: string
}

export default function TaskDetailModal({
  isOpen, onClose, task, projectId, canEdit, canDelete,
}: TaskDetailModalProps) {
  const { updateTask, deleteTask, fetchComments, addComment, fetchBoard } = useProjectStore()
  const [editMode, setEditMode] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<TaskStatus>('todo')
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [assigneeId, setAssigneeId] = useState<number | ''>('')
  const [startDate, setStartDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [completionPercent, setCompletionPercent] = useState(0)
  const [users, setUsers] = useState<UserOption[]>([])
  const [comments, setComments] = useState<TaskComment[]>([])
  const [newComment, setNewComment] = useState('')
  const [savingComment, setSavingComment] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen || !task) return
    setTitle(task.title || '')
    setDescription(task.description || '')
    setStatus(task.status)
    setPriority(task.priority)
    setAssigneeId(task.assignee_id || '')
    setStartDate(task.start_date || '')
    setDueDate(task.due_date || '')
    setCompletionPercent(task.completion_percent || 0)
    setEditMode(false)
    setError('')

    apiGet<{ items: UserOption[] }>('/projects/users')
      .then((res) => setUsers(res.items || []))
      .catch(() => {})

    fetchComments(task.id).then(setComments).catch(() => setComments([]))
  }, [isOpen, task, fetchComments])

  if (!isOpen || !task) return null

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      await updateTask(task.id, {
        title,
        description,
        status,
        priority,
        assignee_id: assigneeId === '' ? null : Number(assigneeId),
        start_date: startDate || null,
        due_date: dueDate || null,
        completion_percent: completionPercent,
      })
      setEditMode(false)
      await fetchBoard(projectId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm(`Delete task "${task.title}"?`)) return
    try {
      await deleteTask(task.id)
      onClose()
    } catch {
      alert('Failed to delete task')
    }
  }

  const handleAddComment = async () => {
    const content = newComment.trim()
    if (!content) return
    setSavingComment(true)
    try {
      const c = await addComment(task.id, content)
      setComments([c, ...comments])
      setNewComment('')
    } finally {
      setSavingComment(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden rounded-t-xl sm:rounded-xl bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-sm font-medium text-gray-500">Task #{task.id}</h2>
          <div className="flex items-center gap-1">
            {canDelete && !editMode && (
              <button onClick={handleDelete} className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label="Delete task">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button onClick={onClose} className="rounded p-1.5 hover:bg-gray-100" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          {editMode ? (
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-lg font-semibold focus:border-primary-500 focus:outline-none"
            />
          ) : (
            <h1 className="text-lg font-semibold text-gray-900">{task.title}</h1>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Status">
              {editMode ? (
                <select value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)} className="w-full rounded border border-gray-300 px-2 py-1 text-sm">
                  {TASK_STATUS_ORDER.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              ) : (
                <span className="capitalize">{task.status.replace('_', ' ')}</span>
              )}
            </Field>
            <Field label="Priority">
              {editMode ? (
                <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)} className="w-full rounded border border-gray-300 px-2 py-1 text-sm">
                  {TASK_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              ) : (
                <span className="capitalize">{task.priority}</span>
              )}
            </Field>
            <Field label="Assignee" icon={UserIcon}>
              {editMode ? (
                <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value === '' ? '' : Number(e.target.value))} className="w-full rounded border border-gray-300 px-2 py-1 text-sm">
                  <option value="">— Unassigned —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              ) : (
                task.assignee_name || <span className="text-gray-400">Unassigned</span>
              )}
            </Field>
            <Field label="Completion %">
              {editMode ? (
                <input type="number" min={0} max={100} value={completionPercent}
                       onChange={(e) => setCompletionPercent(Number(e.target.value))}
                       className="w-full rounded border border-gray-300 px-2 py-1 text-sm" />
              ) : (
                <span>{task.completion_percent || 0}%</span>
              )}
            </Field>
            <Field label="Start Date" icon={Calendar}>
              {editMode ? (
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                       className="w-full rounded border border-gray-300 px-2 py-1 text-sm" />
              ) : (
                task.start_date ? new Date(task.start_date).toLocaleDateString() : <span className="text-gray-400">—</span>
              )}
            </Field>
            <Field label="Due Date" icon={Calendar}>
              {editMode ? (
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                       className="w-full rounded border border-gray-300 px-2 py-1 text-sm" />
              ) : (
                task.due_date ? new Date(task.due_date).toLocaleDateString() : <span className="text-gray-400">—</span>
              )}
            </Field>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
            {editMode ? (
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            ) : (
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.description || <span className="text-gray-400">No description</span>}</p>
            )}
          </div>

          <div className="border-t border-gray-200 pt-3">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-gray-500" />
              <p className="text-sm font-medium text-gray-700">Comments ({comments.length})</p>
            </div>
            <div className="space-y-2 mb-3">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment…"
                rows={2}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="flex justify-end">
                <button
                  onClick={handleAddComment}
                  disabled={savingComment || !newComment.trim()}
                  className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {savingComment ? 'Posting…' : 'Post'}
                </button>
              </div>
            </div>
            <ul className="space-y-2">
              {comments.map((c) => (
                <li key={c.id} className="rounded-lg bg-gray-50 px-3 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-gray-700">{c.user_name || `User #${c.user_id}`}</p>
                    <p className="text-[10px] text-gray-500">{new Date(c.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.content}</p>
                </li>
              ))}
              {comments.length === 0 && (
                <li className="text-xs text-gray-400 text-center py-2">No comments yet</li>
              )}
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          {editMode ? (
            <>
              <button onClick={() => setEditMode(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            canEdit && (
              <button onClick={() => setEditMode(true)} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
                Edit
              </button>
            )
          )}
        </div>
      </div>
    </div>
  )
}

interface FieldProps {
  label: string
  icon?: React.ElementType
  children: React.ReactNode
}

function Field({ label, icon: Icon, children }: FieldProps) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </p>
      <div className="text-sm text-gray-800">{children}</div>
    </div>
  )
}
